import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth';
import { Building2, CreditCard, Save, Loader2, User, MapPin, Percent, Wallet, Smartphone, Calendar, CalendarClock, Lock, Eye, EyeOff, Sparkles, Plus, Trash2 } from 'lucide-react';

/**
 * Client-side mirror of backend/iban_utils.py::validate_italian_iban.
 * Kept intentionally simple (no external deps): checks format + ISO 13616
 * mod-97 checksum for Italian IBANs (IT + 25 chars). The backend is the
 * source of truth — this is only a UX hint.
 */
function isValidItalianIban(raw) {
    if (!raw) return false;
    const iban = String(raw).replace(/\s+/g, '').toUpperCase();
    if (iban.length !== 27) return false;
    if (!/^IT[0-9]{2}[A-Z][0-9]{10}[0-9A-Z]{12}$/.test(iban)) return false;
    // ISO 13616 mod-97: move 4 leading chars to the end, map A-Z -> 10-35, mod 97 == 1.
    const rearr = iban.slice(4) + iban.slice(0, 4);
    let expanded = '';
    for (const ch of rearr) {
        if (ch >= '0' && ch <= '9') expanded += ch;
        else expanded += String(ch.charCodeAt(0) - 55);
    }
    // Big-int-safe modulo (process 9 digits at a time to stay within safe int)
    let remainder = 0;
    for (let i = 0; i < expanded.length; i += 7) {
        remainder = Number(String(remainder) + expanded.slice(i, i + 7)) % 97;
    }
    return remainder === 1;
}

export default function Profilo() {
    const { t } = useTranslation();
    const { user, refreshUser } = useAuth();
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        company_name: '', piva: '', sdi_code: '', address: '', cap: '', citta: '', provincia: '',
        iban: '', banca_nome: '', bic_swift: '', anticipo_default_pct: 20,
        full_name: '', phone: '',
        paypal_me: '', satispay_link: '', rateizza_attivo: false, rateizza_info: '',
        commissioni_a_carico: 'artigiano', commissioni_pct: 1.4,
        default_payment_terms: '30gg_fine_mese',
        outreach_sender_name: '',
    });

    // --- Password change state ---
    const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
    const [pwdShow, setPwdShow] = useState({ current: false, next: false, confirm: false });
    const [pwdSaving, setPwdSaving] = useState(false);

    // --- Quote preferences (AI customization) state ---
    const [prefs, setPrefs] = useState({
        ai_areas_focus: '',
        ai_style_notes: '',
        custom_voci: [], // [{id, tipo, descrizione, unita, prezzo_unitario, categoria_tag}]
    });
    const [prefsSaving, setPrefsSaving] = useState(false);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    useEffect(() => {
        if (user) {
            setForm({
                company_name: user.company_name || '',
                piva: user.piva || '',
                sdi_code: user.sdi_code || '0000000',
                address: user.address || '',
                cap: user.cap || '',
                citta: user.citta || '',
                provincia: user.provincia || '',
                iban: user.iban || '',
                banca_nome: user.banca_nome || '',
                bic_swift: user.bic_swift || '',
                anticipo_default_pct: user.anticipo_default_pct ?? 20,
                full_name: user.full_name || '',
                phone: user.phone || '',
                paypal_me: user.paypal_me || '',
                satispay_link: user.satispay_link || '',
                rateizza_attivo: !!user.rateizza_attivo,
                rateizza_info: user.rateizza_info || '',
                commissioni_a_carico: user.commissioni_a_carico || 'artigiano',
                commissioni_pct: user.commissioni_pct ?? 1.4,
                default_payment_terms: user.default_payment_terms || '30gg_fine_mese',
                outreach_sender_name: user.outreach_sender_name || '',
            });
        }
    }, [user]);

    // Fetch quote preferences on mount
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/auth/me/quote-preferences');
                if (cancelled) return;
                setPrefs({
                    ai_areas_focus: res.data?.ai_areas_focus || '',
                    ai_style_notes: res.data?.ai_style_notes || '',
                    custom_voci: Array.isArray(res.data?.custom_voci) ? res.data.custom_voci : [],
                });
            } catch {
                // silent (first time, no prefs yet)
            } finally {
                if (!cancelled) setPrefsLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/auth/me/company', {
                ...form,
                anticipo_default_pct: parseFloat(form.anticipo_default_pct) || 0,
                commissioni_pct: parseFloat(form.commissioni_pct) || 0,
            });
            toast.success(t('pages.profilo_saved'));
            if (refreshUser) await refreshUser();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally {
            setSaving(false);
        }
    };

    // --- Password handlers ---
    const handlePwdSubmit = async (e) => {
        e.preventDefault();
        if (pwdForm.new_password.length < 6) {
            toast.error(t('pages.profilo_pwd_min'));
            return;
        }
        if (pwdForm.new_password !== pwdForm.confirm_password) {
            toast.error(t('pages.profilo_pwd_mismatch'));
            return;
        }
        if (pwdForm.new_password === pwdForm.current_password) {
            toast.error(t('pages.profilo_pwd_same'));
            return;
        }
        setPwdSaving(true);
        try {
            await api.post('/auth/me/password', pwdForm);
            toast.success(t('pages.profilo_pwd_updated'));
            setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
        } catch (err) {
            toast.error(err.response?.data?.detail || t('common.generic_error'));
        } finally {
            setPwdSaving(false);
        }
    };

    // --- Quote prefs handlers ---
    const addVoce = () => {
        setPrefs((p) => ({
            ...p,
            custom_voci: [
                ...p.custom_voci,
                {
                    id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    tipo: 'manodopera',
                    descrizione: '',
                    unita: 'h',
                    prezzo_unitario: 0,
                    categoria_tag: '',
                },
            ],
        }));
    };

    const updateVoce = (idx, patch) => {
        setPrefs((p) => {
            const next = [...p.custom_voci];
            next[idx] = { ...next[idx], ...patch };
            return { ...p, custom_voci: next };
        });
    };

    const removeVoce = (idx) => {
        setPrefs((p) => ({ ...p, custom_voci: p.custom_voci.filter((_, i) => i !== idx) }));
    };

    const savePrefs = async () => {
        setPrefsSaving(true);
        try {
            const payload = {
                ai_areas_focus: prefs.ai_areas_focus || '',
                ai_style_notes: prefs.ai_style_notes || '',
                custom_voci: (prefs.custom_voci || [])
                    .filter((v) => (v.descrizione || '').trim().length > 0)
                    .map((v) => ({
                        id: v.id && !v.id.startsWith('tmp_') ? v.id : undefined,
                        tipo: v.tipo || 'manodopera',
                        descrizione: v.descrizione || '',
                        unita: v.unita || 'pz',
                        prezzo_unitario: parseFloat(v.prezzo_unitario) || 0,
                        categoria_tag: v.categoria_tag || '',
                    })),
            };
            const res = await api.put('/auth/me/quote-preferences', payload);
            toast.success(t('pages.profilo_ai_quote_saved'));
            if (res.data) {
                setPrefs({
                    ai_areas_focus: res.data.ai_areas_focus || '',
                    ai_style_notes: res.data.ai_style_notes || '',
                    custom_voci: Array.isArray(res.data.custom_voci) ? res.data.custom_voci : [],
                });
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally {
            setPrefsSaving(false);
        }
    };

    return (
        <div data-testid="profilo-page" className="pb-20 md:pb-0 max-w-4xl">
            <div className="mb-6">
                <h1 className="font-display text-4xl md:text-5xl">{t('pages.profilo_title')}</h1>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.profilo_subtitle')}</p>
            </div>

            {/* Section: Anagrafica titolare */}
            <Card className="p-5 mb-4">
                <h2 className="font-display text-xl flex items-center gap-2 mb-4 pb-2 border-b border-[hsl(var(--border))]">
                    <User className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.profilo_section_owner')}
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <Label>{t('pages.profilo_full_name')}</Label>
                        <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1 h-11" data-testid="profilo-full-name" />
                    </div>
                    <div>
                        <Label>{t('pages.profilo_phone')}</Label>
                        <Input type="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 333 1234567" className="mt-1 h-11" />
                    </div>
                </div>
            </Card>

            {/* Section: Anagrafica impresa */}
            <Card className="p-5 mb-4">
                <h2 className="font-display text-xl flex items-center gap-2 mb-4 pb-2 border-b border-[hsl(var(--border))]">
                    <Building2 className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.profilo_section_company')}
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <Label>{t('pages.profilo_company_name')}</Label>
                        <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1 h-11" data-testid="profilo-company-name" />
                    </div>
                    <div>
                        <Label className="flex items-center gap-1">
                            <Sparkles className="w-3 h-3 text-[hsl(var(--accent))]" /> {t('pages.profilo_outreach_sender_name')}
                        </Label>
                        <Input
                            value={form.outreach_sender_name}
                            onChange={(e) => setForm({ ...form, outreach_sender_name: e.target.value })}
                            placeholder="CantiereAI"
                            className="mt-1 h-11"
                            data-testid="profilo-outreach-sender-name"
                        />
                        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 leading-snug">
                            {t('pages.profilo_outreach_sender_name_hint')}
                        </p>
                    </div>
                    <div>
                        <Label>{t('pages.profilo_piva')}</Label>
                        <Input value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} placeholder="12345678901" className="mt-1 h-11" data-testid="profilo-piva" />
                    </div>
                    <div>
                        <Label>{t('pages.profilo_sdi')}</Label>
                        <Input maxLength={7} value={form.sdi_code} onChange={(e) => setForm({ ...form, sdi_code: e.target.value })} className="mt-1 h-11" />
                    </div>
                </div>
                <div className="mt-3">
                    <Label className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {t('pages.profilo_address')}</Label>
                    <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 h-11" />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                    <Input placeholder={t('pages.profilo_cap')} value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} className="h-11" />
                    <Input placeholder={t('pages.profilo_citta')} value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} className="h-11" />
                    <Input placeholder={t('pages.profilo_provincia')} maxLength={2} value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase() })} className="h-11" />
                </div>
            </Card>

            {/* Section: Dati bancari */}
            <Card className="p-5 mb-4 border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5">
                <h2 className="font-display text-xl flex items-center gap-2 mb-4 pb-2 border-b border-[hsl(var(--border))]">
                    <CreditCard className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.profilo_section_bank')}
                </h2>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{t('pages.profilo_bank_hint')}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                        <Label>{t('pages.profilo_iban')} *</Label>
                        <Input
                            value={form.iban}
                            onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase().replace(/\s+/g, '') })}
                            placeholder="IT60X0542811101000000123456"
                            className={`mt-1 h-11 font-mono tracking-wider ${
                                form.iban && !isValidItalianIban(form.iban)
                                    ? 'border-[hsl(0_72%_52%)] focus-visible:ring-[hsl(0_72%_52%)]'
                                    : form.iban && isValidItalianIban(form.iban)
                                        ? 'border-[hsl(145_55%_34%)] focus-visible:ring-[hsl(145_55%_34%)]'
                                        : ''
                            }`}
                            data-testid="profilo-iban"
                            maxLength={27}
                        />
                        {form.iban && !isValidItalianIban(form.iban) && (
                            <div className="mt-1 text-xs text-[hsl(0_72%_52%)] flex items-center gap-1" data-testid="profilo-iban-error">
                                {t('pages.profilo_iban_error') || 'IBAN non valido. Formato atteso: IT + 25 caratteri (27 in totale).'}
                            </div>
                        )}
                        {form.iban && isValidItalianIban(form.iban) && (
                            <div className="mt-1 text-xs text-[hsl(145_55%_34%)] flex items-center gap-1" data-testid="profilo-iban-valid">
                                ✓ {t('pages.profilo_iban_valid') || 'IBAN italiano valido'}
                            </div>
                        )}
                        <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                            {t('pages.profilo_iban_required_hint') || 'Obbligatorio per emettere fatture: il pagamento delle fatture ai clienti finali avviene esclusivamente tramite bonifico bancario su questo IBAN.'}
                        </div>
                    </div>
                    <div>
                        <Label>{t('pages.profilo_banca')}</Label>
                        <Input value={form.banca_nome} onChange={(e) => setForm({ ...form, banca_nome: e.target.value })} placeholder="Intesa Sanpaolo" className="mt-1 h-11" data-testid="profilo-banca" />
                    </div>
                    <div>
                        <Label>{t('pages.profilo_bic')}</Label>
                        <Input value={form.bic_swift} onChange={(e) => setForm({ ...form, bic_swift: e.target.value.toUpperCase() })} placeholder="BCITITMM" className="mt-1 h-11 font-mono" />
                    </div>
                </div>
            </Card>

            {/* Section: Anticipo default */}
            <Card className="p-5 mb-4">
                <h2 className="font-display text-xl flex items-center gap-2 mb-4 pb-2 border-b border-[hsl(var(--border))]">
                    <Percent className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.profilo_section_anticipo')}
                </h2>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{t('pages.profilo_anticipo_hint')}</p>
                <div className="flex items-end gap-3 max-w-xs">
                    <div className="flex-1">
                        <Label>{t('pages.profilo_anticipo_pct')}</Label>
                        <div className="relative mt-1">
                            <Input type="number" inputMode="decimal" min="0" max="100" step="1" value={form.anticipo_default_pct} onChange={(e) => setForm({ ...form, anticipo_default_pct: e.target.value })} className="h-11 pr-8" data-testid="profilo-anticipo-pct" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[hsl(var(--muted-foreground))]">%</span>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Section: Default payment terms */}
            <Card className="p-5 mb-4">
                <h2 className="font-display text-xl flex items-center gap-2 mb-4 pb-2 border-b border-[hsl(var(--border))]">
                    <CalendarClock className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.profilo_section_default_pt') || 'Termine di pagamento predefinito'}
                </h2>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{t('pages.profilo_default_pt_hint') || 'Applicato automaticamente a nuove proforme e fatture (modificabile per ogni documento).'}</p>
                <div className="max-w-xs">
                    <Label>{t('pages.prev_pt_label')}</Label>
                    <Select value={form.default_payment_terms} onValueChange={(v) => setForm({ ...form, default_payment_terms: v })}>
                        <SelectTrigger className="mt-1 h-11" data-testid="profilo-default-payment-terms">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="vista_fattura">{t('pages.prev_pt_vista_fattura')}</SelectItem>
                            <SelectItem value="fine_mese">{t('pages.prev_pt_fine_mese')}</SelectItem>
                            <SelectItem value="30gg_fine_mese">{t('pages.prev_pt_30gg_fine_mese')}</SelectItem>
                            <SelectItem value="60gg_fine_mese">{t('pages.prev_pt_60gg_fine_mese')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </Card>

            {/* Section: Online payments */}
            <Card className="p-5 mb-4">
                <h2 className="font-display text-xl flex items-center gap-2 mb-2 pb-2 border-b border-[hsl(var(--border))]">
                    <Wallet className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.profilo_section_pay')}
                </h2>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{t('pages.profilo_pay_hint')}</p>

                <div className="space-y-4">
                    <div>
                        <Label className="flex items-center gap-2"><span className="inline-block w-5 h-5 rounded bg-[#0070BA] text-white text-[10px] font-bold flex items-center justify-center">P</span> {t('pages.profilo_paypal')}</Label>
                        <Input value={form.paypal_me} onChange={(e) => setForm({ ...form, paypal_me: e.target.value })} placeholder={t('pages.profilo_paypal_ph')} className="mt-1 h-11" data-testid="profilo-paypal" />
                    </div>
                    <div>
                        <Label className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-[#E91E50]" /> {t('pages.profilo_satispay')}</Label>
                        <Input value={form.satispay_link} onChange={(e) => setForm({ ...form, satispay_link: e.target.value })} placeholder={t('pages.profilo_satispay_ph')} className="mt-1 h-11" data-testid="profilo-satispay" />
                    </div>
                    <div className="pt-3 border-t border-[hsl(var(--border))]">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={form.rateizza_attivo} onChange={(e) => setForm({ ...form, rateizza_attivo: e.target.checked })} className="w-4 h-4 accent-[hsl(var(--accent))]" data-testid="profilo-rateizza-attivo" />
                            <Calendar className="w-4 h-4 text-[hsl(var(--accent))]" />
                            <span className="font-semibold">{t('pages.profilo_rateizza_attivo')}</span>
                        </label>
                        {form.rateizza_attivo && (
                            <div className="mt-2 ml-6">
                                <Label className="text-xs">{t('pages.profilo_rateizza_info')}</Label>
                                <Textarea rows={2} value={form.rateizza_info} onChange={(e) => setForm({ ...form, rateizza_info: e.target.value })} placeholder={t('pages.profilo_rateizza_info_ph')} className="mt-1" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Commissioni */}
                <div className="mt-5 pt-4 border-t border-[hsl(var(--border))]">
                    <Label className="font-semibold">{t('pages.profilo_commissioni_carico')}</Label>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                        <label className={`flex items-center gap-2 p-3 rounded-md border cursor-pointer ${form.commissioni_a_carico === 'artigiano' ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5' : 'border-[hsl(var(--border))]'}`}>
                            <input type="radio" name="comm" value="artigiano" checked={form.commissioni_a_carico === 'artigiano'} onChange={(e) => setForm({ ...form, commissioni_a_carico: e.target.value })} className="accent-[hsl(var(--accent))]" />
                            <span className="text-sm">{t('pages.profilo_commissioni_artigiano')}</span>
                        </label>
                        <label className={`flex items-center gap-2 p-3 rounded-md border cursor-pointer ${form.commissioni_a_carico === 'cliente' ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5' : 'border-[hsl(var(--border))]'}`}>
                            <input type="radio" name="comm" value="cliente" checked={form.commissioni_a_carico === 'cliente'} onChange={(e) => setForm({ ...form, commissioni_a_carico: e.target.value })} className="accent-[hsl(var(--accent))]" />
                            <span className="text-sm">{t('pages.profilo_commissioni_cliente')}</span>
                        </label>
                    </div>
                    {form.commissioni_a_carico === 'cliente' && (
                        <div className="mt-3 max-w-xs">
                            <Label className="text-xs">{t('pages.profilo_commissioni_pct')}</Label>
                            <div className="relative mt-1">
                                <Input type="number" inputMode="decimal" min="0" max="20" step="0.1" value={form.commissioni_pct} onChange={(e) => setForm({ ...form, commissioni_pct: e.target.value })} className="h-11 pr-8" data-testid="profilo-commissioni-pct" />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[hsl(var(--muted-foreground))]">%</span>
                            </div>
                            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t('pages.profilo_commissioni_pct_hint')}</p>
                        </div>
                    )}
                </div>
            </Card>

            {/* Section: Sicurezza (cambio password) */}
            <Card className="p-5 mb-4" data-testid="profilo-section-security">
                <h2 className="font-display text-xl flex items-center gap-2 mb-2 pb-2 border-b border-[hsl(var(--border))]">
                    <Lock className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.profilo_section_security')}
                </h2>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{t('pages.profilo_section_security_hint')}</p>
                <form onSubmit={handlePwdSubmit} className="space-y-3 max-w-md">
                    <div>
                        <Label>{t('pages.profilo_pwd_current')}</Label>
                        <div className="relative mt-1">
                            <Input
                                type={pwdShow.current ? 'text' : 'password'}
                                value={pwdForm.current_password}
                                onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                                className="h-11 pr-10"
                                autoComplete="current-password"
                                data-testid="profilo-pwd-current"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setPwdShow({ ...pwdShow, current: !pwdShow.current })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                                aria-label="toggle"
                                tabIndex={-1}
                            >
                                {pwdShow.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <Label>{t('pages.profilo_pwd_new')}</Label>
                        <div className="relative mt-1">
                            <Input
                                type={pwdShow.next ? 'text' : 'password'}
                                value={pwdForm.new_password}
                                onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                                className="h-11 pr-10"
                                placeholder={t('pages.profilo_pwd_new_ph')}
                                autoComplete="new-password"
                                data-testid="profilo-pwd-new"
                                required
                                minLength={6}
                            />
                            <button
                                type="button"
                                onClick={() => setPwdShow({ ...pwdShow, next: !pwdShow.next })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                                aria-label="toggle"
                                tabIndex={-1}
                            >
                                {pwdShow.next ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <Label>{t('pages.profilo_pwd_confirm')}</Label>
                        <div className="relative mt-1">
                            <Input
                                type={pwdShow.confirm ? 'text' : 'password'}
                                value={pwdForm.confirm_password}
                                onChange={(e) => setPwdForm({ ...pwdForm, confirm_password: e.target.value })}
                                className="h-11 pr-10"
                                autoComplete="new-password"
                                data-testid="profilo-pwd-confirm"
                                required
                                minLength={6}
                            />
                            <button
                                type="button"
                                onClick={() => setPwdShow({ ...pwdShow, confirm: !pwdShow.confirm })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                                aria-label="toggle"
                                tabIndex={-1}
                            >
                                {pwdShow.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <Button
                        type="submit"
                        disabled={pwdSaving || !pwdForm.current_password || !pwdForm.new_password || !pwdForm.confirm_password}
                        className="bg-[hsl(220_13%_10%)] hover:bg-[hsl(220_13%_20%)] text-white font-semibold h-11 gap-2"
                        data-testid="profilo-pwd-submit"
                    >
                        {pwdSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                        {pwdSaving ? t('pages.profilo_pwd_updating') : t('pages.profilo_pwd_button')}
                    </Button>
                </form>
            </Card>

            {/* Section: Personalizza voci preventivo (AI customization) */}
            <Card className="p-5 mb-4 border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5" data-testid="profilo-section-ai-quote">
                <h2 className="font-display text-xl flex items-center gap-2 mb-2 pb-2 border-b border-[hsl(var(--border))]">
                    <Sparkles className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.profilo_section_ai_quote')}
                </h2>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{t('pages.profilo_ai_quote_hint')}</p>

                <div className="space-y-3 mb-5">
                    <div>
                        <Label>{t('pages.profilo_ai_areas')}</Label>
                        <Input
                            value={prefs.ai_areas_focus}
                            onChange={(e) => setPrefs({ ...prefs, ai_areas_focus: e.target.value })}
                            placeholder={t('pages.profilo_ai_areas_ph')}
                            className="mt-1 h-11"
                            data-testid="profilo-ai-areas"
                            maxLength={500}
                        />
                    </div>
                    <div>
                        <Label>{t('pages.profilo_ai_style')}</Label>
                        <Textarea
                            rows={3}
                            value={prefs.ai_style_notes}
                            onChange={(e) => setPrefs({ ...prefs, ai_style_notes: e.target.value })}
                            placeholder={t('pages.profilo_ai_style_ph')}
                            className="mt-1"
                            data-testid="profilo-ai-style"
                            maxLength={1500}
                        />
                    </div>
                </div>

                {/* Custom voci listino */}
                <div className="mt-4 border-t border-[hsl(var(--border))] pt-4">
                    <div className="flex items-start justify-between gap-3 mb-1">
                        <div>
                            <h3 className="font-semibold text-base">{t('pages.profilo_custom_voci_title')}</h3>
                            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{t('pages.profilo_custom_voci_hint')}</p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={addVoce}
                            className="flex-shrink-0 h-9 gap-1.5 border-[hsl(var(--accent))]/40 hover:border-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10"
                            data-testid="profilo-voce-add"
                        >
                            <Plus className="w-4 h-4" /> {t('pages.profilo_voce_add')}
                        </Button>
                    </div>

                    {prefsLoaded && prefs.custom_voci.length === 0 && (
                        <div className="mt-3 p-6 rounded-lg border border-dashed border-[hsl(var(--border))] bg-white text-center" data-testid="profilo-voci-empty">
                            <Sparkles className="w-8 h-8 text-[hsl(var(--muted-foreground))] mx-auto mb-2 opacity-60" />
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.profilo_custom_voci_empty')}</p>
                        </div>
                    )}

                    {prefs.custom_voci.length > 0 && (
                        <div className="mt-3 space-y-3" data-testid="profilo-voci-list">
                            {prefs.custom_voci.map((v, idx) => (
                                <div key={v.id || idx} className="p-3 rounded-md border border-[hsl(var(--border))] bg-white" data-testid={`profilo-voce-row-${idx}`}>
                                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                                        <div className="sm:col-span-5">
                                            <Label className="text-[11px] text-[hsl(var(--muted-foreground))]">{t('pages.profilo_voce_descrizione')}</Label>
                                            <Input
                                                value={v.descrizione}
                                                onChange={(e) => updateVoce(idx, { descrizione: e.target.value })}
                                                placeholder={t('pages.profilo_voce_descrizione_ph')}
                                                className="mt-1 h-10"
                                                data-testid={`profilo-voce-descrizione-${idx}`}
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Label className="text-[11px] text-[hsl(var(--muted-foreground))]">{t('pages.profilo_voce_tipo')}</Label>
                                            <Select value={v.tipo} onValueChange={(val) => updateVoce(idx, { tipo: val })}>
                                                <SelectTrigger className="mt-1 h-10" data-testid={`profilo-voce-tipo-${idx}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="manodopera">{t('pages.profilo_voce_tipo_manodopera')}</SelectItem>
                                                    <SelectItem value="materiali">{t('pages.profilo_voce_tipo_materiali')}</SelectItem>
                                                    <SelectItem value="attrezzatura">{t('pages.profilo_voce_tipo_attrezzatura')}</SelectItem>
                                                    <SelectItem value="smaltimento">{t('pages.profilo_voce_tipo_smaltimento')}</SelectItem>
                                                    <SelectItem value="trasporto">{t('pages.profilo_voce_tipo_trasporto')}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="sm:col-span-1">
                                            <Label className="text-[11px] text-[hsl(var(--muted-foreground))]">{t('pages.profilo_voce_unita')}</Label>
                                            <Select value={v.unita} onValueChange={(val) => updateVoce(idx, { unita: val })}>
                                                <SelectTrigger className="mt-1 h-10" data-testid={`profilo-voce-unita-${idx}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {['h', 'mq', 'ml', 'kg', 'pz', 'n', 'gg', 'cad', 'mc', 'corpo'].map((u) => (
                                                        <SelectItem key={u} value={u}>{u}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Label className="text-[11px] text-[hsl(var(--muted-foreground))]">{t('pages.profilo_voce_prezzo')}</Label>
                                            <Input
                                                type="number"
                                                inputMode="decimal"
                                                min="0"
                                                step="0.01"
                                                value={v.prezzo_unitario}
                                                onChange={(e) => updateVoce(idx, { prezzo_unitario: e.target.value })}
                                                className="mt-1 h-10"
                                                data-testid={`profilo-voce-prezzo-${idx}`}
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Label className="text-[11px] text-[hsl(var(--muted-foreground))]">{t('pages.profilo_voce_tag')}</Label>
                                            <Input
                                                value={v.categoria_tag}
                                                onChange={(e) => updateVoce(idx, { categoria_tag: e.target.value })}
                                                placeholder={t('pages.profilo_voce_tag_ph')}
                                                className="mt-1 h-10"
                                                data-testid={`profilo-voce-tag-${idx}`}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => removeVoce(idx)}
                                            className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(0_70%_50%)] inline-flex items-center gap-1 px-2 py-1 rounded transition-colors"
                                            data-testid={`profilo-voce-remove-${idx}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> {t('pages.profilo_voce_remove')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mt-4 flex justify-end">
                        <Button
                            type="button"
                            onClick={savePrefs}
                            disabled={prefsSaving}
                            className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-11 gap-2"
                            data-testid="profilo-prefs-save"
                        >
                            {prefsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            {t('pages.profilo_ai_quote_save')}
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Save bar */}
            <div className="sticky bottom-4 md:bottom-0 md:static z-10 mt-4 flex justify-end">
                <Button onClick={save} disabled={saving} className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-12 px-8 gap-2 shadow-lg" data-testid="profilo-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {t('pages.profilo_save')}
                </Button>
            </div>
        </div>
    );
}
